import { useLoadAllItemsQuery } from '@sequencehq/api'
import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'
import {
  Badge,
  Center,
  GreyGrey30,
  Primary13Regular,
  ShadowXs,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@sequencehq/core-components'
import {
  localDateWithFormat,
  toCreditGrantStatusBadgeProps,
  toMoney
} from '@sequencehq/utils'
import { apiDatesAdapters, format } from '@sequencehq/utils/dates'
import { DATE_FORMAT } from 'Customer/drawers/CreateCustomerCreditGrant/createCustomerCreditGrant.constants'
import { useCustomerContext } from 'Customer/hooks/useCustomerContext'
import { CreditGrantsEmptyState } from './CreditGrantsEmptyState'

type CreditGrantV2 = Dashboardv99990101Api.CreditGrants.CreditGrantV2

/**
 * The customer's own V2 credit grants, laid out like the V1 table. Grants on
 * the customer's schedules come back from the same list, but they belong on the
 * schedule page, so they are left out here.
 */
export const CustomerCreditGrantTable = () => {
  const { customer } = useCustomerContext()
  const { data, hasNextPage, isPending } = useLoadAllItemsQuery(
    dashboardv99990101Client.getCreditGrants,
    { customerId: customer.id, limit: 100 }
  )

  // Schedule grants are filtered out here rather than by the API, so a page can
  // hold none of the customer's own. Showing pages as they arrive could show
  // the empty state, or a short list, for a customer who has grants.
  const isLoadingGrants = isPending || hasNextPage

  if (isLoadingGrants) {
    return (
      <Center height="200px" width="100%" mt={4}>
        <Spinner />
      </Center>
    )
  }

  const customerGrants = (data?.pages ?? [])
    .flatMap(page => page?.items ?? [])
    .filter(grant => grant.scope === 'CUSTOMER')

  return (
    <TableContainer
      borderRadius="lg"
      overflow="hidden"
      border={`1px solid ${GreyGrey30}`}
      mt={4}
      width="100%"
      boxShadow={ShadowXs}
      data-testid="customerCreditGrants.table"
    >
      <Table variant="v2" width="100%">
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th width={120}>Status</Th>
            <Th width={175}>Balance</Th>
            <Th width={120}>Price charged</Th>
            <Th width={120}>Issued</Th>
            <Th width={120}>Expiry date</Th>
          </Tr>
        </Thead>
        <Tbody>
          {customerGrants.length > 0 ? (
            customerGrants.map(grant => (
              <CustomerCreditGrantRow key={grant.id} grant={grant} />
            ))
          ) : (
            <Tr
              sx={{
                cursor: 'auto!important',
                _hover: {
                  bgColor: 'inherit!important'
                }
              }}
            >
              <Td colSpan={6}>
                <CreditGrantsEmptyState />
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </TableContainer>
  )
}

const CustomerCreditGrantRow = ({ grant }: { grant: CreditGrantV2 }) => {
  const formatMoney = (value: number) =>
    toMoney({ value: value.toString(), currency: grant.currency })

  // A date-only value, read as a local date so it doesn't shift a day west of
  // UTC the way `localDateWithFormat` would.
  const expiryDate = grant.expiryDate
    ? apiDatesAdapters.fromApi(grant.expiryDate)
    : undefined

  return (
    <Tr data-testid={`creditGrant.${grant.id}`}>
      <Td>
        <Text {...Primary13Regular}>{grant.name}</Text>
      </Td>
      <Td>
        <Badge
          {...toCreditGrantStatusBadgeProps({
            status: grant.status,
            size: 'sm'
          })}
          data-testid={`creditGrant.${grant.id}.status`}
        />
      </Td>
      <Td data-testid={`creditGrant.${grant.id}.balance`}>
        {`${formatMoney(grant.remainingBalance)} / ${formatMoney(grant.amount)}`}
      </Td>
      <Td data-testid={`creditGrant.${grant.id}.priceCharged`}>
        {grant.costOfCredit !== undefined
          ? formatMoney(grant.costOfCredit)
          : '-'}
      </Td>
      <Td>{localDateWithFormat(grant.createdAt, DATE_FORMAT)}</Td>
      <Td data-testid={`creditGrant.${grant.id}.expiryDate`}>
        {expiryDate ? format(expiryDate, DATE_FORMAT) : 'Never'}
      </Td>
    </Tr>
  )
}
